import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { costReportQuery, type CostMonth, type Money } from '../../api/settingsAiAgent'
import { Spinner } from '../../ui/Spinner'
import { failed, shortNum } from './parts'

/**
 * Fila "Cost report": cat a costat Adam, luna asta si luna trecuta, pe firma,
 * pe om si pe model. Cifrele vin din `it_ai_messages` cu preturile din
 * `Audit::cost` - aceleasi ca in "Usage by lab" si in jurnalul de audit.
 *
 * Administratorul platformei vede toate firmele, in USD. Adminul unei firme
 * vede numai firma lui, in forma aleasa pentru ea (USD, credite, sau fara
 * sume). Poarta e pe server.
 */
export function CostReportTab() {
  const query = useQuery(costReportQuery())
  const [at, setAt] = useState(0)

  if (query.isError) return <p className="text-danger">{failed(query.error)}</p>
  if (!query.data) return <Spinner />

  const report = query.data
  const month = report.months[at] ?? null
  const hidden = report.form === 'hidden'

  return (
    <div className="panel panel-default aia-cost-report">
      <div className="panel-heading">
        <strong>Cost report</strong>
        <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
          {report.scope === 'platform' ? 'every company, in USD' : 'this company'}
          {hidden ? ' - amounts are hidden for this company' : ''}
        </span>
      </div>
      <div className="panel-body" style={{ fontSize: 13 }}>
        <div className="btn-group" role="group" style={{ marginBottom: 10 }}>
          {report.months.map((m, i) => (
            <button
              key={m.label}
              type="button"
              className={`btn btn-default btn-xs${i === at ? ' active' : ''}`}
              onClick={() => setAt(i)}
            >
              {i === 0 ? 'This month' : 'Last month'} ({m.label})
            </button>
          ))}
        </div>
        {month ? <MonthView month={month} platform={report.scope === 'platform'} hidden={hidden} /> : null}
      </div>
    </div>
  )
}

function Amount({ money }: { money: Money | null }) {
  if (!money || money.text === '') return <span className="text-muted">-</span>

  return <span>{money.text}</span>
}

function MonthView({ month, platform, hidden }: { month: CostMonth; platform: boolean; hidden: boolean }) {
  return (
    <>
      {!hidden ? (
        <p data-testid="cost-total">
          Total: <strong>{month.total.text}</strong>
        </p>
      ) : null}
      {month.unpriced.length > 0 ? (
        <p className="text-warning" style={{ fontSize: 12 }}>
          No price written for: {month.unpriced.join(', ')} - their cost is not counted.
        </p>
      ) : null}

      {platform ? (
        <>
          <h5>By company</h5>
          <table className="table table-condensed" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th>Company</th>
                <th>Cost</th>
                <th>Budget</th>
                <th>Used</th>
              </tr>
            </thead>
            <tbody>
              {month.companies.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>
                    <Amount money={c.cost} />
                  </td>
                  <td>{c.budget ? c.budget.text : <span className="text-muted">not set</span>}</td>
                  <td>{c.pct === null ? '' : `${c.pct}%`}</td>
                </tr>
              ))}
              {month.companies.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-muted">
                    Nothing this month.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </>
      ) : null}

      <h5>By person</h5>
      <table className="table table-condensed" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            {platform ? <th>Company</th> : null}
            <th>Person</th>
            {!hidden ? <th>Cost</th> : null}
          </tr>
        </thead>
        <tbody>
          {month.users.map((u) => (
            <tr key={u.company_id + ':' + u.user_id}>
              {platform ? <td>{u.company}</td> : null}
              <td>{u.name}</td>
              {!hidden ? (
                <td>
                  <Amount money={u.cost} />
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>

      <h5>By model</h5>
      <table className="table table-condensed" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>Model</th>
            <th>Answers</th>
            <th>Tokens in / out</th>
            <th>Cache read</th>
            {!hidden ? <th>Cost</th> : null}
          </tr>
        </thead>
        <tbody>
          {month.models.map((m) => (
            <tr key={m.model}>
              <td>{m.model || '(unknown)'}</td>
              <td>{m.messages}</td>
              <td>
                {shortNum(m.tokens_in)} / {shortNum(m.tokens_out)}
              </td>
              <td>{shortNum(m.cache_read)}</td>
              {!hidden ? <td>{m.priced ? <Amount money={m.cost} /> : <span className="text-muted">no price</span>}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}
